import React from 'react';
import { ArrowRight, BookOpenCheck, HeartHandshake, GraduationCap, School } from 'lucide-react';

// Choose Your Journey — four clear entry points into the academy.
// Children (3–6) • Parents • Educators (Teachers) • Schools & Institutions

const journeys = [
  {
    id: 'children',
    icon: BookOpenCheck,
    tag: 'For Children (3–6)',
    title: 'For My Child',
    description:
      'Structured phonics, early reading, readiness, vocabulary and comprehension for young learners.',
    cta: 'Explore Children\u2019s Programmes',
    target: 'programs',
    accent: 'from-[#3B4BA0] to-indigo-600',
    ring: 'hover:border-[#3B4BA0]',
    iconBg: 'bg-[#3B4BA0]',
    btn: 'bg-[#3B4BA0] hover:bg-[#2d3a7a]'
  },
  {
    id: 'parents',
    icon: HeartHandshake,
    tag: 'For Parents',
    title: 'For Me as a Parent',
    description:
      'Simple, practical guidance so you can confidently support your child\u2019s reading journey at home.',
    cta: 'Guidance for Parents',
    target: 'learn-to-teach',
    accent: 'from-rose-500 to-pink-500',
    ring: 'hover:border-rose-400',
    iconBg: 'bg-rose-500',
    btn: 'bg-rose-500 hover:bg-rose-600'
  },
  {
    id: 'educators',
    icon: GraduationCap,
    tag: 'For Teachers & Aspiring Educators',
    title: 'For Me as an Educator',
    description:
      'Practical, classroom-tested phonics and early literacy training you can use with real children right away.',
    cta: 'Explore Educator Training',
    target: 'learn-to-teach',
    accent: 'from-teal-500 to-emerald-500',
    ring: 'hover:border-teal-500',
    iconBg: 'bg-teal-600',
    btn: 'bg-teal-600 hover:bg-teal-700'
  },
  {
    id: 'schools',
    icon: School,
    tag: 'For Schools & Institutions',
    title: 'For My School or Centre',
    description:
      'Teacher training, curriculum guidance and speaking engagements for preschools, kindergartens and centres.',
    cta: 'Partner With Read Right',
    target: 'for-schools',
    accent: 'from-amber-500 to-orange-500',
    ring: 'hover:border-amber-500',
    iconBg: 'bg-amber-500',
    btn: 'bg-amber-500 hover:bg-amber-600'
  }
];

const ChooseYourJourney = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="choose-your-journey"
      data-testid="choose-your-journey"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Choose Your Journey
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Where Would You Like to Begin?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Whether you&apos;re a parent, teacher, or school, there is a starting
            point at Read Right Academy that fits you.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {journeys.map((j) => {
            const Icon = j.icon;
            return (
              <div
                key={j.id}
                data-testid={`journey-${j.id}`}
                className={`group relative bg-white rounded-3xl border-2 border-slate-100 ${j.ring} hover:shadow-2xl transition-all duration-500 p-6 sm:p-7 flex flex-col overflow-hidden`}
              >
                <div
                  aria-hidden="true"
                  className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${j.accent} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`}
                />
                <div className="relative flex-1 flex flex-col">
                  <div
                    className={`w-12 h-12 rounded-2xl ${j.iconBg} text-white flex items-center justify-center shadow-md mb-5`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                    {j.tag}
                  </p>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-snug">
                    {j.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                    {j.description}
                  </p>
                  <button
                    onClick={() => scrollTo(j.target)}
                    data-testid={`journey-cta-${j.id}`}
                    className={`inline-flex items-center justify-center gap-2 w-full ${j.btn} text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-300`}
                  >
                    {j.cta}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ChooseYourJourney;
